import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, CreditCard } from 'lucide-react';
import type { SubscriptionPlan } from '@/hooks/usePayment';
import { useInitiateCollectCheckout, usePaymentConfig } from '@/hooks/usePayment';

interface NmiCollectCheckoutProps {
  plan: SubscriptionPlan | null;
  disabled?: boolean;
}

function loadCollectCheckoutScript(scriptUrl: string, checkoutKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const w = window as unknown as Record<string, unknown>;
    if (w.CollectCheckout) {
      resolve();
      return;
    }

    const existing = document.querySelector('script[src*="CollectCheckout.js"]');
    if (existing) {
      const checkReady = () => {
        if (w.CollectCheckout) {
          resolve();
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
      return;
    }

    console.log('[NmiCollectCheckout] Loading script:', { scriptUrl, keyPrefix: checkoutKey?.slice(0, 20) });

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.setAttribute('data-checkout-key', checkoutKey);
    script.async = true;

    script.onload = () => {
      const checkReady = () => {
        if (w.CollectCheckout) {
          resolve();
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    };

    script.onerror = () => reject(new Error('Failed to load Collect Checkout'));
    document.head.appendChild(script);
  });
}

export function NmiCollectCheckout({ plan, disabled }: NmiCollectCheckoutProps) {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const initiateCheckout = useInitiateCollectCheckout();
  const { data: paymentConfig, isLoading: configLoading } = usePaymentConfig();

  const handleCheckout = useCallback(async () => {
    if (!plan) {
      setError('No plan selected');
      return;
    }

    if (!paymentConfig?.checkoutKey) {
      setError('Checkout key is not configured');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const redirectUrl = `${window.location.origin}/subscribe/success`;
      const result = await initiateCheckout.mutateAsync({
        planId: plan.id,
        redirectUrl,
      });

      const config = result.config;

      await loadCollectCheckoutScript(paymentConfig.scriptUrl, paymentConfig.checkoutKey);

      const CollectCheckout = (window as unknown as Record<string, unknown>).CollectCheckout as
        | { redirectToCheckout: (opts: unknown) => Promise<unknown> }
        | undefined;

      if (!CollectCheckout) {
        throw new Error('CollectCheckout is not available');
      }

      localStorage.setItem('pendingCheckoutPlanId', plan.id);

      await CollectCheckout.redirectToCheckout({
        lineItems: config.lineItems,
        type: config.type,
        customerVault: config.customerVault,
        successUrl: config.successUrl,
        cancelUrl: config.cancelUrl,
        receipt: {
          sendToCustomer: true,
          showReceipt: true,
          redirectToSuccessUrl: true,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start checkout';
      setError(message);
      setIsLoading(false);
    }
  }, [plan, initiateCheckout, paymentConfig]);

  return (
    <div className="space-y-2">
      <Button
        className="w-full"
        onClick={handleCheckout}
        disabled={disabled || isLoading || configLoading}
      >
        {isLoading || configLoading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <CreditCard className="mr-2 h-4 w-4" />
        )}
        {isLoading || configLoading ? 'Preparing secure checkout...' : 'Subscribe with Card, Google Pay, or Apple Pay'}
      </Button>
      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
      <p className="text-xs text-muted-foreground text-center">
        Secure checkout powered by NMI. You will be redirected to a secure payment page.
      </p>
    </div>
  );
}
